import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { AddCircleOutlineOutlined, RemoveCircleOutlineOutlined } from '@mui/icons-material';
import SaveAsRoundedIcon from '@mui/icons-material/SaveAsRounded';
import { LoadingButton } from '@mui/lab';
import {
  Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, FormControl, FormControlLabel, FormGroup, FormHelperText, FormLabel,
  IconButton,
  InputLabel, MenuItem,
  Paper, Select,
  SelectChangeEvent,
  Stack, Switch,
  TextField,
  Theme,
  ThemeOptions,
  Typography,
  useTheme
} from '@mui/material';
import { AxiosError } from 'axios';
import { addDays, endOfDay, startOfDay } from 'date-fns';
import { format } from 'date-fns-tz';
import { useSnackbar } from 'notistack';
import { ChangeEvent, FC, memo, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Controller, SubmitHandler, useFieldArray, useForm, UseFormReturn } from 'react-hook-form';
import LicenseContext from 'src/contexts/LicenseContext';
import { CompanyEntity } from 'src/entities/admin/companyEntity';
import {
  DesignatedTruckCarModel, DesignatedTruckFeature, DesignatedTruckFloorSpecification, DesignatedTruckKlass, DesignatedTruckLoadingPlatformHeight,
  DesignatedTruckLoadingPlatformLength, DesignatedTruckLoadingPlatformWidth, ItemHandlingOfCargoStyle,
  ItemKlass, ItemPackageStyle, LoadingDateTimeEntity, OrderCustomInputField } from 'src/entities/orderEntity';
import { PlaceEntity, SpecifiedTimeEntity } from 'src/entities/placeEntity';
import { SalesOrderBillingPaymentAmount, SalesOrderCandidatePartnerCompany, SalesOrderLicense, SalesOrderRecurrenceRule, SalesOrderResponseEntity, SalesOrderSpecifiedDateTime } from 'src/entities/SalesOrder.response.entity';
import { ShipperEntity } from 'src/entities/shipperEntity';
import { usePartnerCompaniesRequest } from 'src/hooks/usePartnerCompanies.request';
import { useQueryCompany } from 'src/hooks/useQueryCompany';
import { useQueryPlaces } from 'src/hooks/useQueryPlaces';
import { useMutationSalesOrder } from 'src/hooks/useQuerySalesOrders';
import { useQueryShippers } from 'src/hooks/useQueryShippers';
import * as yup from 'yup';

import { ConfirmDialog } from '../dialogs/SalesOrders/Confirm.dialog';
import MultiCheckbox from '../MultiCheckBox';
import CandidateCompanySelector from '../selectors/CandidateCompanySelector';
import PartnerCompanySelector from '../selectors/PartnerCompanySelector';

import RepeatSelect from './RepeatSelect';

function validateLoadUnloadDateTimeRelation(values: { start_at?: Date; end_at?: Date; }[]): boolean {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, react/no-this-in-sfc
  const loadingTimes: LoadingDateTimeEntity[] = this.from[0].value.salesOrderLoadingSpecifiedDatetimesAttributes;
  const minLoadingStartAt = loadingTimes.map((e) => (e.start_at)).reduce((a, b) => (a < b ? a : b));
  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
  const maxUnloadingEndAt = values.map((e) => (e.end_at)).reduce((a, b) => (a < b ? b : a));
  return !(new Date(maxUnloadingEndAt) < new Date(minLoadingStartAt));
}

const loadingDateTime = yup.object().shape({
  start_at: yup
    .date().typeError('日時を入力してください')
    .required('必須です'),
  end_at: yup
    .date().typeError('日時を入力してください')
    .min(yup.ref('start_at'), '受付開始時間より遅い時間を指定してください')
    .required('必須です')
});

function validateNoOverWrap(values: {start_at: Date, end_at: Date}[]): boolean {
  if (values.length === 1) {
    return true;
  }
  const sorted = values.sort((a, b) => a.start_at.getTime() - b.start_at.getTime());
  const results = sorted.map((it, index) => {
    const nextValue = sorted[index + 1];
    if (nextValue === undefined) return true;

    return it.end_at <= nextValue.start_at;
  });
  return results.every((it) => it);
}

function validateWithinOneMonth(values: { start_at: Date, end_at: Date}[]): boolean {
  return values.every(({ start_at: startAt, end_at: endAt }) => {
    const oneMonthLater = new Date(startAt);
    oneMonthLater.setMonth(oneMonthLater.getMonth() + 1);
    return endAt <= oneMonthLater;
  });
}

const billingPaymentAmountSchema = {
  id: yup.number().transform((value:number, originalValue: number) => (String(originalValue).trim() === '' ? null : value)).nullable(),
  basicFeeYen: yup.number().transform((value:number, originalValue: number) => (String(originalValue).trim() === '' ? null : value)).nullable(),
  surchargeFeeYen: yup.number().transform((value:number, originalValue: number) => (String(originalValue).trim() === '' ? null : value)).nullable(),
  relayFeeYen: yup.number().transform((value:number, originalValue: number) => (String(originalValue).trim() === '' ? null : value)).nullable(),
  highwayFeeYen: yup.number().transform((value:number, originalValue: number) => (String(originalValue).trim() === '' ? null : value)).nullable(),
  loadingFeeYen: yup.number().transform((value:number, originalValue: number) => (String(originalValue).trim() === '' ? null : value)).nullable(),
  ancillaryFeeYen: yup.number().transform((value:number, originalValue: number) => (String(originalValue).trim() === '' ? null : value)).nullable(),
  waitingTimeFeeYen: yup.number().transform((value:number, originalValue: number) => (String(originalValue).trim() === '' ? null : value)).nullable(),
  unloadingFeeYen: yup.number().transform((value:number, originalValue: number) => (String(originalValue).trim() === '' ? null : value)).nullable(),
  expensesFeeYen: yup.number().transform((value:number, originalValue: number) => (String(originalValue).trim() === '' ? null : value)).nullable(),
  ancillaryContent: yup.string().transform((value:string, originalValue: string) => (String(originalValue).trim() === '' ? null : value)).nullable(),
};

const schema = yup.object({
  companyId: yup.number().required('必須です'),
  statusJa: yup.string().required('必須です'),
  shipperName: yup.string(),
  designatedTruckCarModels: yup.array().of(yup.string()),
  designatedTruckFeatures: yup.array().of(yup.string()),
  designatedTruckFloor_specifications: yup.array().of(yup.string()),
  designatedTruckLoadingPlatformHeights: yup.array().of(yup.string()),
  designatedTruckLoadingPlatformLengths: yup.array().of(yup.string()),
  designatedTruckLoadingPlatformWidths: yup.array().of(yup.string()),
  designatedTruckKlasses: yup.array().of(yup.string()),
  itemCount: yup
    .number()
    .transform((value:number, originalValue: number) => (String(originalValue).trim() === '' ? null : value))
    .nullable()
    .typeError('数字を入力してください')
    .moreThan(-1, '0以上の数値を入力してください'),
  itemTotalWeightKg: yup
    .number()
    .transform((value:number, originalValue: number) => (String(originalValue).trim() === '' ? null : value))
    .nullable()
    .typeError('数字を入力してください')
    .moreThan(-1, '0以上の数値を入力してください'),
  truckCount: yup
    .number()
    .transform((value:number, originalValue: number) => (String(originalValue).trim() === '' ? null : value))
    .nullable()
    .typeError('数字を入力してください')
    .moreThan(-1, '0以上の数値を入力してください'),
  loadingName: yup.string(),
  loadingAddress: yup.string(),
  loadingStayingMinutss: yup
    .number()
    .integer()
    .nullable()
    .moreThan(-1, '0以上の数値を入力してください')
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((val) => ((!!val || val === 0) ? val : null)),
  unloadingName: yup.string(),
  unloadingAddress: yup.string(),
  unloadingStayingMinutes: yup
    .number()
    .moreThan(-1, '0以上の数値を入力してください')
    .integer()
    .nullable()
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    .transform((val) => ((!!val || val === 0) ? val : null)),
  salesOrderLoadingSpecifiedDatetimesAttributes: yup
    .array()
    .of(loadingDateTime)
    .test(
      'timeoverlap',
      '積地受付時間帯に重複する時間帯があります',
      validateNoOverWrap,
    )
    .test(
      'withinOneMonth',
      '開始から終了まで1ヶ月以内に設定してください。',
      validateWithinOneMonth,
    )
    .test(
      'startAtAfter2000',
      '開始時間は2000年以降の日時を設定してください。',
      (values) => values.every(({ start_at: startAt }) => new Date(startAt).getFullYear() >= 2000)
    ),
  salesOrderUnloadingSpecifiedDatetimesAttributes: yup
    .array()
    .of(loadingDateTime)
    .test(
      'timeoverlap',
      '降地受付時間帯に重複する時間帯があります',
      validateNoOverWrap,
    )
    .test(
      'loadUnloadRelation',
      'いずれかの降地受付終了時間は、もっとも早い積地受付開始時間よりもあとの時間を指定してください',
      validateLoadUnloadDateTimeRelation
    )
    .test(
      'withinOneMonth',
      '開始から終了まで1ヶ月以内に設定してください。',
      validateWithinOneMonth,
    )
    .test(
      'startAtAfter2000',
      '開始時間は2000年以降の日時を設定してください。',
      (values) => values.every(({ start_at: startAt }) => new Date(startAt).getFullYear() >= 2000)
    ),
    salesOrderBillingAmountAttributes: yup.object().shape(billingPaymentAmountSchema),
    salesOrderPaymentAmountAttributes: yup.object().shape(billingPaymentAmountSchema),
});

type SalesOrderFormProps = {
  dialogIsOpen: boolean;
  dialogOnClose: () => void;
  onClose?: () => void;
  entity?: SalesOrderResponseEntity;
};

const SalesOrderForm: FC<SalesOrderFormProps> = memo(
  ({
    dialogIsOpen,
    dialogOnClose,
    onClose,
    entity,
  }) => {
    const licenseContext = useContext(LicenseContext);
    const theme: Theme & ThemeOptions = useTheme();
    const { data: partners } = usePartnerCompaniesRequest(0, '', '', dialogIsOpen, 10000);

    const formatYMDHM = useCallback((date: Date | string) => {
      if (!date) return '';

      return format(
        new Date(date),
        "yyyy-MM-dd'T'HH:mm",
        {
          timeZone: 'Asia/Tokyo'
        }
      );
    }, []);

    const form: UseFormReturn<SalesOrderResponseEntity> = useForm<SalesOrderResponseEntity>({
      resolver: yupResolver(schema),
      defaultValues: {
        statusJa: '案件',
        itemCanBeMixed: true,
        salesOrderLoadingSpecifiedDatetimesAttributes: (entity && entity.salesOrderLoadingSpecifiedDatetimesAttributes.length > 0)
          ? entity.salesOrderLoadingSpecifiedDatetimesAttributes.map((dt) => (
            {
              start_at: formatYMDHM(dt.start_at),
              end_at: formatYMDHM(dt.end_at),
            }
          )) : [{
            start_at: formatYMDHM(startOfDay(new Date())),
            end_at: formatYMDHM(endOfDay(new Date())),
          }],
        salesOrderUnloadingSpecifiedDatetimesAttributes: (entity && entity.salesOrderUnloadingSpecifiedDatetimesAttributes.length > 0)
          ? entity.salesOrderUnloadingSpecifiedDatetimesAttributes.map((dt) => (
            {
              start_at: formatYMDHM(dt.start_at),
              end_at: formatYMDHM(dt.end_at),
            }
          )) : [{
            start_at: formatYMDHM(startOfDay(new Date())),
            end_at: formatYMDHM(endOfDay(new Date())),
          }],
      }
    });

    const { formState, control, reset, register, watch, getValues, setValue } = form;
    const { errors } = formState;
    const [loadingStartTimeError, setLoadingStartTimeError] = useState<string>('');
    const [unloadingStartTimeError, setUnloadingStartTimeError] = useState<string>('');
    const [unloadingEndTimeError, setUnloadingEndTimeError] = useState<string>('');

    useEffect(() => {
      if (errors.salesOrderLoadingSpecifiedDatetimesAttributes?.type === 'timeoverlap'
        || errors.salesOrderLoadingSpecifiedDatetimesAttributes?.type === 'withinOneMonth'
        || errors.salesOrderLoadingSpecifiedDatetimesAttributes?.type === 'startAtAfter2000'
      ) {
        setLoadingStartTimeError(errors.salesOrderLoadingSpecifiedDatetimesAttributes.message);
      } else {
        setLoadingStartTimeError('');
      }
      if (errors.salesOrderUnloadingSpecifiedDatetimesAttributes?.type === 'timeoverlap'
        || errors.salesOrderUnloadingSpecifiedDatetimesAttributes?.type === 'withinOneMonth'
        || errors.salesOrderUnloadingSpecifiedDatetimesAttributes?.type === 'startAtAfter2000'
      ) {
        setUnloadingStartTimeError(errors.salesOrderUnloadingSpecifiedDatetimesAttributes.message);
      } else {
        setUnloadingStartTimeError('');
      }
      if (errors.salesOrderUnloadingSpecifiedDatetimesAttributes?.type === 'loadUnloadRelation') {
        setUnloadingEndTimeError(errors.salesOrderUnloadingSpecifiedDatetimesAttributes.message);
      } else {
        setUnloadingEndTimeError('');
      }
    }, [errors]);

    const { enqueueSnackbar } = useSnackbar();

    const [companyId, setCompanyId] = useState(0);

    const places = useQueryPlaces(dialogIsOpen);
    const shippers = useQueryShippers(dialogIsOpen);
    const { data: companyData } = useQueryCompany(companyId);

    const { add: addSalesOrder, update: updateSalesOrder } = useMutationSalesOrder();

    const watchFields = watch(['itemCount', 'itemPackingStyle']);

    const [modified, setModified] = useState(false);
    const [itemPackingStyleValue, setItemPackingStyleValue] = useState<string>(null);
    const [itemKlass, setItemKlass] = useState<string>(null);
    const [itemHandlingOfCargoStyle, setItemHandlingOfCargoStyle] = useState<string>(null);
    const [itemCanBeMixed, setItemCanBeMixed] = useState<boolean>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [designatedTruckKlasses, setDesignatedTruckKlasses] = useState<DesignatedTruckKlass[]>([]);
    const [designatedTruckCarModels, setDesignatedTruckCarModels] = useState<DesignatedTruckCarModel[]>([]);
    const [designatedTruckLoadingPlatformHeights, setDesignatedTruckLoadingPlatformHeights] = useState<DesignatedTruckLoadingPlatformHeight[]>([]);
    const [designatedTruckLoadingPlatformWidths, setDesignatedTruckLoadingPlatformWidths] = useState<DesignatedTruckLoadingPlatformWidth[]>([]);
    const [designatedTruckLoadingPlatformLengths, setDesignatedTruckLoadingPlatformLengths] = useState<DesignatedTruckLoadingPlatformLength[]>([]);
    const [designatedTruckFloorSpecifications, setDesignatedTruckFloorSpecifications] = useState<DesignatedTruckFloorSpecification[]>([]);
    const [designatedTruckFeatures, setDesignatedTruckFeatures] = useState<DesignatedTruckFeature[]>([]);
    const [recurrenceRule, setRecurrenceRule] = useState<SalesOrderRecurrenceRule>(null);

    const [customInputFieldOne, setCustomInputFieldOne] = useState<string>('');
    const updateCustomInputFieldOne = (event: ChangeEvent<HTMLInputElement>) => {
      setCustomInputFieldOne(event.target.value);
    };
    const [customInputFieldTwo, setCustomInputFieldTwo] = useState<string>('');
    const updateCustomInputFieldTwo = (event: ChangeEvent<HTMLInputElement>) => {
      setCustomInputFieldTwo(event.target.value);
    };
    const [customInputFieldThree, setCustomInputFieldThree] = useState<string>('');
    const updateCustomInputFieldThree = (event: ChangeEvent<HTMLInputElement>) => {
      setCustomInputFieldThree(event.target.value);
    };
    const [customInputFieldFour, setCustomInputFieldFour] = useState<string>('');
    const updateCustomInputFieldFour = (event: ChangeEvent<HTMLInputElement>) => {
      setCustomInputFieldFour(event.target.value);
    };
    const [customInputFieldFive, setCustomInputFieldFive] = useState<string>('');
    const updateCustomInputFieldFive = (event: ChangeEvent<HTMLInputElement>) => {
      setCustomInputFieldFive(event.target.value);
    };

    const onChangeSetModified = (event: ChangeEvent<HTMLInputElement>) => {
      setModified(true);
      if (event.target.name === 'itemPackingStyle') {
        setItemPackingStyleValue(event.target.value);
      } else if (event.target.name === 'itemKlass') {
        setItemKlass(event.target.value);
      } else if (event.target.name === 'itemHandlingOfCargoStyle') {
        setItemHandlingOfCargoStyle(event.target.value);
      } else if (event.target.name === 'itemCanBeMixed') {
        setItemCanBeMixed(event.target.checked);
      }
    };

    const [confirmDialogIsOpen, setConfirmDialogIsOpen] = useState(false);
    const [pendingFormData, setPendingFormData] = useState<SalesOrderResponseEntity>(null);

    const handleConfirmApply = () => {
      setConfirmDialogIsOpen(false);
      pendingFormData.applyToOrders = true;
      handleSaveSubmit(pendingFormData);
    };

    const handleConfirmNotApply = () => {
      setConfirmDialogIsOpen(false);
      pendingFormData.applyToOrders = false;
      handleSaveSubmit(pendingFormData);
    };

    const handleConfirmCancel = () => {
      setConfirmDialogIsOpen(false);
      setPendingFormData(null);
    };

    const onSubmitSave: SubmitHandler<SalesOrderResponseEntity> = (
      data: SalesOrderResponseEntity
    ): void => {
      if ((!entity || entity.statusJa !== data.statusJa) && data.statusJa === '受注確定') {
        setPendingFormData(data);
        setConfirmDialogIsOpen(true);
        return;
      }
      handleSaveSubmit(data);
    };

    const handleSaveSubmit = (data: SalesOrderResponseEntity) => {
      const mutate = data.id ? updateSalesOrder : addSalesOrder;

      data.itemPackingStyle = itemPackingStyleValue;
      data.itemKlass = itemKlass as ItemKlass;
      data.itemHandlingOfCargoStyle = itemHandlingOfCargoStyle as ItemHandlingOfCargoStyle;
      data.itemCanBeMixed = itemCanBeMixed;

      data.designatedTruckKlasses = designatedTruckKlasses;
      data.designatedTruckCarModels = designatedTruckCarModels;
      data.designatedTruckLoadingPlatformHeights = designatedTruckLoadingPlatformHeights;
      data.designatedTruckLoadingPlatformWidths = designatedTruckLoadingPlatformWidths;
      data.designatedTruckLoadingPlatformLengths = designatedTruckLoadingPlatformLengths;
      data.designatedTruckFloorSpecifications = designatedTruckFloorSpecifications;
      data.designatedTruckFeatures = designatedTruckFeatures;

      const customFields = [
        customInputFieldOne,
        customInputFieldTwo,
        customInputFieldThree,
        customInputFieldFour,
        customInputFieldFive,
      ];

      if (companyData && companyData.custom_input_fields) {
        const customInputFields: {
          key: string;
          value: string;
        }[] = companyData
          .custom_input_fields
          .map((it, idx) => {
            if (!customFields[idx]) return null;

            return {
              key: it,
              value: customFields[idx]
            };
          })
          .filter((maybe) => maybe);

        data.customInputFields = customInputFields;
      }

      if (entity) {
        const loadingTimeExistingIds = data.salesOrderLoadingSpecifiedDatetimesAttributes.map((it) => it.id).filter((it) => it);
        entity.salesOrderLoadingSpecifiedDatetimesAttributes?.forEach((it) => {
          if (!loadingTimeExistingIds.includes(it.id)) {
            data.salesOrderLoadingSpecifiedDatetimesAttributes.push({ id: it.id, start_at: '', end_at: '', _destroy: true });
          }
        });
        const unloadingTimeExisitingIds = data.salesOrderUnloadingSpecifiedDatetimesAttributes.map((it) => it.id).filter((it) => it);
        entity.salesOrderUnloadingSpecifiedDatetimesAttributes?.forEach((it) => {
          if (!unloadingTimeExisitingIds.includes(it.id)) {
            data.salesOrderUnloadingSpecifiedDatetimesAttributes.push({ id: it.id, start_at: '', end_at: '', _destroy: true });
          }
        });
      }

      setIsLoading(true);

      mutate.mutateAsync(data).then(() => {
        enqueueSnackbar('保存しました');
        if (onClose) onClose();
      }).catch((error: AxiosError<{ messages: string[] }>) => {
        enqueueSnackbar(error.response.data.messages.join(', '));
      })
        .finally(() => {
          setIsLoading(false);
        });
    };

    const placeData = useMemo(() => (
      places.data ? places.data.pages.flat().flatMap((p) => p.data) : []
    ), [places.data]);

    const shipperData = useMemo(() => (
      shippers.data ? shippers.data.pages.flat().flatMap((p) => p.data) : []
    ), [shippers.data]);

    const complementIfEmpty = (attr: keyof SalesOrderResponseEntity, value: string | number | boolean | SalesOrderSpecifiedDateTime[] | DesignatedTruckCarModel[] | DesignatedTruckFeature[] | DesignatedTruckLoadingPlatformHeight[] | DesignatedTruckLoadingPlatformLength[] | DesignatedTruckLoadingPlatformWidth[] | DesignatedTruckKlass[] | SalesOrderBillingPaymentAmount | DesignatedTruckFloorSpecification[] | OrderCustomInputField[] | SalesOrderLicense | SalesOrderCandidatePartnerCompany[] | SalesOrderRecurrenceRule) => {
      if (!getValues(attr)) {
        setValue(attr, value);
      }
    };

    const calculateStartEndTime = (times: SpecifiedTimeEntity) => {
      const start = startOfDay(addDays(new Date(), times.start_relative_day || 0));
      start.setHours(Number(times.start_at.split(':')[0]));
      start.setMinutes(Number(times.start_at.split(':')[1]));
      let end = startOfDay(addDays(new Date(), times.end_relative_day || 0));
      end.setHours(Number(times.end_at.split(':')[0]));
      end.setMinutes(Number(times.end_at.split(':')[1]));
      if (end.getTime() < start.getTime()) {
        end = addDays(end, 1);
      }
      return { start, end };
    };

    const loadingNameOnSelect = (_: unknown, value: (string | PlaceEntity & { label: string })) => {
      if (typeof value === 'string') return;
      if (!value) return;

      setValue('loadingName', value.name);
      setValue('loadingAddress', value.address);
      setValue('loadingLatitude', value.latitude);
      setValue('loadingLongitude', value.longitude);
      setValue('skipLoadingGeocode', true);
      complementIfEmpty('shipperName', value.shipper_name);
      complementIfEmpty('shipperContactPerson', value.shipper_contact_person);
      complementIfEmpty('shipperPhoneNumber', value.shipper_phone_number);
      complementIfEmpty('shipperEmailAddress', value.shipper_email_address);

      if (value.place_specified_times_attributes && value.place_specified_times_attributes.length > 0) {
        for (let i = (loadingFields.length - 1); i >= 0; i--) {
          loadingRemove(i);
        }
        value.place_specified_times_attributes.forEach((times) => {
          const { start, end } = calculateStartEndTime(times);
          loadingAppend({
            start_at: formatYMDHM(start),
            end_at: formatYMDHM(end)
          });
        });
      }
      complementIfEmpty('loadingStayingMinutes', value.staying_minutes);

      complementPlaceCommon(value);
    };

    const unloadingNameOnSelect = (_: unknown, value: (string | PlaceEntity & { label: string })) => {
      if (typeof value === 'string') return;
      if (!value) return;

      setValue('unloadingName', value.name);
      setValue('unloadingAddress', value.address);
      setValue('unloadingLatitude', value.latitude);
      setValue('unloadingLongitude', value.longitude);
      setValue('skipUnloadingGeocode', true);
      complementIfEmpty('shipperName', value.shipper_name);
      complementIfEmpty('shipperContactPerson', value.shipper_contact_person);
      complementIfEmpty('shipperPhoneNumber', value.shipper_phone_number);
      complementIfEmpty('shipperEmailAddress', value.shipper_email_address);

      if (value.place_specified_times_attributes && value.place_specified_times_attributes.length > 0) {
        for (let i = (unloadingFields.length - 1); i >= 0; i--) {
          unloadingRemove(i);
        }
        value.place_specified_times_attributes.forEach((times) => {
          const { start, end } = calculateStartEndTime(times);
          unloadingAppend({
            start_at: formatYMDHM(start),
            end_at: formatYMDHM(end)
          });
        });
      }
      complementIfEmpty('unloadingStayingMinutes', value.staying_minutes);

      complementPlaceCommon(value);
    };

    const complementPlaceCommon = useCallback((value: PlaceEntity) => {
      if (value.designated_truck_klasses) {
        setDesignatedTruckKlasses(Array.from(new Set([...designatedTruckKlasses, ...value.designated_truck_klasses])));
      }
      if (value.designated_truck_car_models) {
        setDesignatedTruckCarModels(Array.from(new Set([...designatedTruckCarModels, ...value.designated_truck_car_models])));
      }
      if (value.designated_truck_loading_platform_heights) {
        setDesignatedTruckLoadingPlatformHeights(Array.from(new Set([...designatedTruckLoadingPlatformHeights, ...value.designated_truck_loading_platform_heights])));
      }
      if (value.designated_truck_loading_platform_widths) {
        setDesignatedTruckLoadingPlatformWidths(Array.from(new Set([...designatedTruckLoadingPlatformWidths, ...value.designated_truck_loading_platform_widths])));
      }
      if (value.designated_truck_loading_platform_lengths) {
        setDesignatedTruckLoadingPlatformLengths(Array.from(new Set([...designatedTruckLoadingPlatformLengths, ...value.designated_truck_loading_platform_lengths])));
      }
      if (value.designated_truck_floor_specifications) {
        setDesignatedTruckFloorSpecifications(Array.from(new Set([...designatedTruckFloorSpecifications, ...value.designated_truck_floor_specifications])));
      }
      if (value.designated_truck_features) {
        setDesignatedTruckFeatures(Array.from(new Set([...designatedTruckFeatures, ...value.designated_truck_features])));
      }
    }, [
      designatedTruckKlasses, designatedTruckCarModels, designatedTruckLoadingPlatformHeights,
      designatedTruckLoadingPlatformWidths, designatedTruckLoadingPlatformLengths, designatedTruckFloorSpecifications,
      designatedTruckFeatures,
    ]);

    const shipperNameOnSelect = (_: unknown, value: (string | ShipperEntity & { label: string })) => {
      if (typeof value === 'string') return;
      setValue('shipperName', value.name);
      setValue('shipperContactPerson', value.contact_person);
      setValue('shipperEmailAddress', value.email_address);
      setValue('shipperPhoneNumber', value.phone_number);
    };

    useEffect(() => {
      if (places.data) {
        // eslint-disable-next-line no-void
        void places.fetchNextPage();
      }
    }, [places]);

    useEffect(() => {
      if (shippers.data) {
        // eslint-disable-next-line no-void
        void shippers.fetchNextPage();
      }
    }, [shippers]);

    useEffect(() => {
      if (!entity) {
        reset({
          statusJa: '案件',
          shipperName: '',
          companyId: licenseContext.config?.selected_company_id || licenseContext.config?.company_id,
          salesOrderLoadingSpecifiedDatetimesAttributes: [{
              start_at: formatYMDHM(startOfDay(new Date())),
              end_at: formatYMDHM(endOfDay(new Date()))
            }],
          salesOrderUnloadingSpecifiedDatetimesAttributes: [{
              start_at: formatYMDHM(startOfDay(new Date())),
              end_at: formatYMDHM(endOfDay(new Date()))
            }],
        });
        setItemCanBeMixed(true);
        setCompanyId(licenseContext.config?.selected_company_id || licenseContext.config?.company_id);
        setItemPackingStyleValue(null);
        setItemKlass(null);
        setItemHandlingOfCargoStyle(null);
        setItemCanBeMixed(true);
        setDesignatedTruckKlasses([]);
        setDesignatedTruckCarModels([]);
        setDesignatedTruckLoadingPlatformHeights([]);
        setDesignatedTruckLoadingPlatformWidths([]);
        setDesignatedTruckLoadingPlatformLengths([]);
        setDesignatedTruckFloorSpecifications([]);
        setDesignatedTruckFeatures([]);
        setCustomInputFieldOne('');
        setCustomInputFieldTwo('');
        setCustomInputFieldThree('');
        setCustomInputFieldFour('');
        setCustomInputFieldFive('');
        setRecurrenceRule(null);
        setSelectedPartners([]);
        setSelectedFixedCompanyId(null);
        setSelectedFixedCompanyName(null);

        return;
      }

      if (!entity.salesOrderLoadingSpecifiedDatetimesAttributes || entity.salesOrderLoadingSpecifiedDatetimesAttributes.length === 0) {
        entity.salesOrderLoadingSpecifiedDatetimesAttributes = [{
          start_at: formatYMDHM(startOfDay(new Date())),
          end_at: formatYMDHM(endOfDay(new Date()))
        }];
      }
      if (!entity.salesOrderUnloadingSpecifiedDatetimesAttributes || entity.salesOrderUnloadingSpecifiedDatetimesAttributes.length === 0) {
        entity.salesOrderUnloadingSpecifiedDatetimesAttributes = [{
          start_at: formatYMDHM(startOfDay(new Date())),
          end_at: formatYMDHM(endOfDay(new Date()))
        }];
      }
      entity.salesOrderLoadingSpecifiedDatetimesAttributes = entity.salesOrderLoadingSpecifiedDatetimesAttributes.map((dt) => ({
        ...dt,
        start_at: formatYMDHM(dt.start_at),
        end_at: formatYMDHM(dt.end_at),
      }));
      entity.salesOrderUnloadingSpecifiedDatetimesAttributes = entity.salesOrderUnloadingSpecifiedDatetimesAttributes.map((dt) => ({
        ...dt,
        start_at: formatYMDHM(dt.start_at),
        end_at: formatYMDHM(dt.end_at),
      }));

      reset(entity);

      setModified(false);
      setCompanyId(entity.companyId);
      setItemPackingStyleValue(entity.itemPackingStyle);
      setItemKlass(entity.itemKlass);
      setItemHandlingOfCargoStyle(entity.itemHandlingOfCargoStyle);
      setItemCanBeMixed(entity.itemCanBeMixed);

      setDesignatedTruckKlasses(entity.designatedTruckKlasses || []);
      setDesignatedTruckCarModels(entity.designatedTruckCarModels || []);
      setDesignatedTruckLoadingPlatformHeights(entity.designatedTruckLoadingPlatformHeights || []);
      setDesignatedTruckLoadingPlatformWidths(entity.designatedTruckLoadingPlatformWidths || []);
      setDesignatedTruckLoadingPlatformLengths(entity.designatedTruckLoadingPlatformLengths || []);
      setDesignatedTruckFloorSpecifications(entity.designatedTruckFloorSpecifications || []);
      setDesignatedTruckFeatures(entity.designatedTruckFeatures || []);
      setRecurrenceRule(entity.salesOrderRecurrenceRuleAttributes);
      setSelectedPartners(entity.salesOrderCandidatePartnerCompaniesAttributes || []);
      setSelectedFixedCompanyId(entity.partnerCompanyId);
      setSelectedFixedCompanyName(entity.partnerCompanyName);
    }, [entity, licenseContext.config, dialogIsOpen, reset]);

    useEffect(() => {
      if (!companyData) return;
      if (!entity) return;

      if (companyData.custom_input_fields[0]) {
        setCustomInputFieldOne(
          entity.customInputFields?.find((field) => field.key === companyData.custom_input_fields[0])?.value || ''
        );
      }
      if (companyData.custom_input_fields[1]) {
        setCustomInputFieldTwo(
          entity.customInputFields?.find((field) => field.key === companyData.custom_input_fields[1])?.value || ''
        );
      }
      if (companyData.custom_input_fields[2]) {
        setCustomInputFieldThree(
          entity.customInputFields?.find((field) => field.key === companyData.custom_input_fields[2])?.value || ''
        );
      }
      if (companyData.custom_input_fields[3]) {
        setCustomInputFieldFour(
          entity.customInputFields?.find((field) => field.key === companyData.custom_input_fields[3])?.value || ''
        );
      }
      if (companyData.custom_input_fields[4]) {
        setCustomInputFieldFive(
          entity.customInputFields?.find((field) => field.key === companyData.custom_input_fields[4])?.value || ''
        );
      }
    }, [companyData, entity]);

    useEffect(() => {
      if (!watchFields.every((maybe) => maybe)) return;
      if (!companyData) return;

      const count = watchFields[0];
      const name = watchFields[1];
      const maybe = companyData
        .packing_style_and_weight_volumes
        .find((style) => style.name === name);

      if (!maybe) return;

      if (maybe.volume) form.setValue('itemTotalVolumeM3', maybe.volume * count);
      if (maybe.weight) form.setValue('itemTotalWeightKg', maybe.weight * count);
    }, [watchFields, companyData, form]);

    const {
      fields: loadingFields,
      append: loadingAppend,
      remove: loadingRemove
    } = useFieldArray({
      control,
      name: 'salesOrderLoadingSpecifiedDatetimesAttributes'
    });

    const {
      fields: unloadingFields,
      append: unloadingAppend,
      remove: unloadingRemove
    } = useFieldArray({
      control,
      name: 'salesOrderUnloadingSpecifiedDatetimesAttributes'
    });

    const onChangeCompany = useCallback((e: SelectChangeEvent) => {
      setCompanyId(Number(e.target.value));
      setValue('companyId', Number(e.target.value));
    }, [setValue]);

    const [selectableCompanies, setSelectableCompanies] = useState<CompanyEntity[]>([]);
    useEffect(() => {
      setSelectableCompanies(licenseContext.config?.selectable_companies ?? []);
    }, [licenseContext.config]);

    const [filteredPlaceData, setFilteredPlaceData] = useState<PlaceEntity[]>([]);
    useEffect(() => {
      setFilteredPlaceData(
        placeData.filter((place) => place.company_id === companyId)
      );
    }, [placeData, companyId]);

    const filteredPickupPlaceData = filteredPlaceData.filter(p => !p.location_role || p.location_role !== 'unloading');
    const filteredDeliveryPlaceData = filteredPlaceData.filter(p => !p.location_role || p.location_role !== 'loading');

    // 請求金額のフィールド名リスト
    const billingPaymentKeys = useMemo(() => ([
      'basicFeeYen',
      'surchargeFeeYen',
      'relayFeeYen',
      'highwayFeeYen',
      'loadingFeeYen',
      'ancillaryFeeYen',
      'waitingTimeFeeYen',
      'unloadingFeeYen',
      'expensesFeeYen'
    ]), []);

    // watch で請求金額オブジェクトを取得
    const billingValues = watch('salesOrderBillingAmountAttributes');
    const paymentValues = watch('salesOrderPaymentAmountAttributes');

    useEffect(() => {
      setModified(true);
    }, [JSON.stringify(billingValues), JSON.stringify(paymentValues)]);

    // 各フィールドの数値を合計（数字以外の場合は 0 とする）
    const totalBillingAmount = useMemo(() => billingPaymentKeys.reduce((sum, key) => sum + (Number(billingValues?.[key]) || 0), 0), [billingPaymentKeys, billingValues, JSON.stringify(billingValues), dialogIsOpen]);
    const totalPaymentAmount = useMemo(() => billingPaymentKeys.reduce((sum, key) => sum + (Number(paymentValues?.[key]) || 0), 0), [billingPaymentKeys, paymentValues, JSON.stringify(paymentValues), dialogIsOpen]);

    const [timeSettings, setTimeSettings] = useState<{ kind: string, startAt: string, endAt: string }[]>([
      { kind: 'AM', startAt: '08:00', endAt: '12:00' },
      { kind: 'PM', startAt: '13:00', endAt: '18:00' },
      { kind: 'ALL', startAt: '08:00', endAt: '20:00' },
    ]);
    useEffect(() => {
      if (!companyData) return;
      const ranges = companyData.company_order_time_ranges_attributes || [];

      setTimeSettings((prev) => prev.map((it) => {
          const range = ranges.find((rng) => it.kind === rng.kind);
          if (range) {
            return {
              ...it,
              startAt: range.start_at,
              endAt: range.end_at,
            };
          }
          return it;
        }));
    }, [companyData]);

    const handleRepeatSave = (rule: SalesOrderRecurrenceRule) => {
      setValue('salesOrderRecurrenceRuleAttributes', rule);
      setRecurrenceRule(rule);
    };

    const [selectedPartners, setSelectedPartners] = useState<SalesOrderCandidatePartnerCompany[]>([]);
    const [selectedFixedCompanyId, setSelectedFixedCompanyId] = useState<number>(null);
    const [selectedFixedCompanyName, setSelectedFixedCompanyName] = useState<string>(null);

    const handleFixedCompanyConfirm = (newCompanyId: number, companyName: string) => {
      setValue('partnerCompanyId', newCompanyId);
      setValue('partnerCompanyName', companyName);
      setSelectedFixedCompanyId(newCompanyId);
      setSelectedFixedCompanyName(companyName);
      setModified(true);
    };

    return (
      <>
        <ConfirmDialog
          open={confirmDialogIsOpen}
          handleApply={handleConfirmApply}
          handleNotApply={handleConfirmNotApply}
          handleCancel={handleConfirmCancel}
        />
        <Dialog
          open={dialogIsOpen}
          maxWidth="md"
          fullWidth
          onClose={dialogOnClose}
        >
          <DialogContent>
            <Stack
              component="form"
              noValidate
              spacing={1}
            >
              {selectableCompanies.length > 1 && (
                <Paper>
                  <Stack
                    pt={1}
                    pb={2}
                    px={2}
                  >
                    <FormControl
                      fullWidth
                      variant="standard"
                      size="small"
                      sx={{
                        marginTop: theme.spacing(1),
                      }}
                    >
                      <Controller
                        control={control}
                        name="companyId"
                        render={() => (
                          <>
                            <InputLabel>事業所</InputLabel>
                            <Select
                              id="company_id"
                              margin="dense"
                              label="事業所"
                              type="number"
                              fullWidth
                              variant="standard"
                              value={`${companyId}`}
                              onChange={onChangeCompany}
                            >
                              {selectableCompanies.map((company) => (
                                <MenuItem value={company.id} key={company.id}>{company.name}</MenuItem>
                              ))}
                            </Select>
                          </>
                        )}
                      />
                      <FormHelperText error={'company_id' in errors}>{errors.companyId?.message}</FormHelperText>
                    </FormControl>
                  </Stack>
                  <Stack
                    pt={1}
                    pb={2}
                    px={2}
                  >
                    <Stack direction="row" gap={1}>
                      <Typography variant="body1" sx={{ fontSize: '0.9em' }}>案件登録者</Typography>
                      {
                        entity?.id
                          ? (<Typography variant="body1">{entity?.registeredLicense?.personName || entity?.registeredLicense?.email || ''}</Typography>)
                          : (<Typography variant="body1">{licenseContext?.config?.person_name || licenseContext?.config?.email || ''}</Typography>)
                      }
                    </Stack>
                  </Stack>
                </Paper>
              )}
              <Paper>
                <Stack
                  pt={1}
                  pb={2}
                  px={2}
                >
                  <FormControl
                    variant="standard"
                    fullWidth
                    size="small"
                  >
                    <InputLabel>ステータス</InputLabel>
                    <Controller
                      control={control}
                      name="statusJa"
                      render={({ field }) => (
                        <Select
                          id="statusJa"
                          margin="dense"
                          label="ステータス"
                          type="text"
                          fullWidth
                          variant="standard"
                          {...field}
                          onChange={(event:ChangeEvent<HTMLInputElement>) => {
                            onChangeSetModified(event);
                            field.onChange(event);
                          }}
                        >
                          <MenuItem value="案件">案件</MenuItem>
                          <MenuItem value="調整中">調整中</MenuItem>
                          <MenuItem value="対応可能">対応可能</MenuItem>
                          <MenuItem value="受注確定">受注確定</MenuItem>
                          <MenuItem value="失注">失注</MenuItem>
                        </Select>
                      )}
                    />
                  </FormControl>
                  <Stack direction="row" spacing={2} alignItems="center" mt={1}>
                    <CandidateCompanySelector
                      selectedPartners={selectedPartners}
                      partners={partners?.data || []}
                      onConfirm={(newPartners) => {
                        const oldIds = entity.salesOrderCandidatePartnerCompaniesAttributes?.map((it) => it.id) || [];
                        const newIds = newPartners.map((it) => it.id);
                        const removedIds = oldIds.filter((it) => !newIds.includes(it));
                        const removed = removedIds.map((it) => entity.salesOrderCandidatePartnerCompaniesAttributes.find(
                          (partner) => partner.id === it
                        )).map((it) => ({ ...it, _destroy: true }));
                        setValue('salesOrderCandidatePartnerCompaniesAttributes', [...removed, ...newPartners]);
                        setSelectedPartners(newPartners);
                        setModified(true);
                      }}
                    />
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="center" mt={1}>
                    <PartnerCompanySelector
                      selectedCompanyId={selectedFixedCompanyId}
                      selectedCompanyName={selectedFixedCompanyName}
                      partners={partners?.data || []}
                      onConfirm={handleFixedCompanyConfirm}
                    />
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="center" mt={1}>
                    <TextField
                      margin="dense"
                      type="text"
                      fullWidth
                      variant="standard"
                      id="assignment"
                      label="割り当て"
                      {...register('assignment')}
                      error={'assignment' in errors}
                      helperText={errors.assignment?.message}
                      InputLabelProps={{ shrink: true }}
                      onChange={onChangeSetModified}
                    />
                    <TextField
                      margin="dense"
                      type="text"
                      fullWidth
                      variant="standard"
                      id="truckNumber"
                      label="車番"
                      {...register('truckNumber')}
                      error={'truckNumber' in errors}
                      helperText={errors.truckNumber?.message}
                      InputLabelProps={{ shrink: true }}
                      onChange={onChangeSetModified}
                    />
                    <TextField
                      margin="dense"
                      type="text"
                      fullWidth
                      variant="standard"
                      id="driverName"
                      label="ドライバー"
                      {...register('driverName')}
                      error={'driverName' in errors}
                      helperText={errors.driverName?.message}
                      InputLabelProps={{ shrink: true }}
                      onChange={onChangeSetModified}
                    />
                  </Stack>
                  <Stack direction="row" spacing={2} alignItems="center" mt={1}>
                    <TextField
                      margin="dense"
                      type="text"
                      fullWidth
                      variant="standard"
                      id="carrier_company"
                      label="運送会社"
                      {...register('carrierCompany')}
                      error={'carrierCompany' in errors}
                      helperText={errors.carrierCompany?.message}
                      InputLabelProps={{ shrink: true }}
                      onChange={onChangeSetModified}
                    />
                    <TextField
                      margin="dense"
                      type="text"
                      fullWidth
                      variant="standard"
                      id="operatingCarrier"
                      label="実走行会社"
                      {...register('operatingCarrier')}
                      error={'operatingCarrier' in errors}
                      helperText={errors.operatingCarrier?.message}
                      InputLabelProps={{ shrink: true }}
                      onChange={onChangeSetModified}
                    />
                    <FormControl
                      variant="standard"
                      fullWidth
                      size="small"
                    >
                      <InputLabel>階層</InputLabel>
                      <Controller
                        control={control}
                        name="hierarcheyLevel"
                        defaultValue=""
                        render={({ field }) => (
                          <Select
                            id="hierarcheyLevel"
                            margin="dense"
                            label="階層"
                            type="number"
                            fullWidth
                            variant="standard"
                            value={field.value || ''}
                            {...field}
                            onChange={(event:SelectChangeEvent<number>) => {
                              setModified(true);
                              field.onChange(event);
                            }}
                          >
                            <MenuItem value="">&nbsp;</MenuItem>
                            <MenuItem value={1}>1次請け</MenuItem>
                            <MenuItem value={2}>2次請け</MenuItem>
                            <MenuItem value={3}>3次請け</MenuItem>
                          </Select>
                        )}
                      />
                    </FormControl>
                  </Stack>
                </Stack>
              </Paper>

              <Paper>
                <Stack
                  pt={1}
                  pb={2}
                  px={2}
                >
                  <TextField
                    margin="dense"
                    type="text"
                    fullWidth
                    variant="standard"
                    id="code"
                    label="注文番号"
                    {...register('code')}
                    error={'code' in errors}
                    helperText={errors.code?.message}
                    InputLabelProps={{ shrink: true }}
                    onChange={onChangeSetModified}
                  />
                  <TextField
                    margin="dense"
                    type="text"
                    fullWidth
                    variant="standard"
                    id="memo"
                    label="備考"
                    {...register('memo')}
                    error={'memo' in errors}
                    helperText={errors.memo?.message}
                    multiline
                    rows={4}
                    InputLabelProps={{ shrink: true }}
                    onChange={onChangeSetModified}
                  />
                  {
                    companyData
                    && companyData.custom_input_fields
                    && companyData.custom_input_fields.length > 0
                    && (
                      <Stack
                        direction={{
                          sm: 'column',
                          md: 'row'
                        }}
                        gap={1}
                        spacing={1}
                      >
                        {
                          companyData.custom_input_fields[0] && (
                            <TextField
                              label={companyData.custom_input_fields[0]}
                              value={customInputFieldOne}
                              onChange={updateCustomInputFieldOne}
                              size="small"
                              variant="standard"
                            />
                          )
                        }
                        {
                          companyData.custom_input_fields[1] && (
                            <TextField
                              label={companyData.custom_input_fields[1]}
                              value={customInputFieldTwo}
                              onChange={updateCustomInputFieldTwo}
                              size="small"
                              variant="standard"
                            />
                          )
                        }
                        {
                          companyData.custom_input_fields[2] && (
                            <TextField
                              label={companyData.custom_input_fields[2]}
                              value={customInputFieldThree}
                              onChange={updateCustomInputFieldThree}
                              size="small"
                              variant="standard"
                            />
                          )
                        }
                        {
                          companyData.custom_input_fields[3] && (
                            <TextField
                              label={companyData.custom_input_fields[3]}
                              value={customInputFieldFour}
                              onChange={updateCustomInputFieldFour}
                              size="small"
                              variant="standard"
                            />
                          )
                        }
                        {
                          companyData.custom_input_fields[4] && (
                            <TextField
                              label={companyData.custom_input_fields[4]}
                              value={customInputFieldFive}
                              onChange={updateCustomInputFieldFive}
                              size="small"
                              variant="standard"
                            />
                          )
                        }
                      </Stack>
                    )
                  }
                </Stack>
              </Paper>
              <Paper>
                <Stack
                  pt={1}
                  pb={2}
                  px={2}
                >
                  <Autocomplete
                    freeSolo
                    options={shipperData.map((d) => ({
                      ...d, label: d.name
                    }))}
                    value={getValues('shipperName') || null}
                    onChange={shipperNameOnSelect}
                    multiple={false}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        margin="dense"
                        type="text"
                        fullWidth
                        variant="standard"
                        label="荷主名"
                        id="shipperName"
                        {...register('shipperName')}
                        error={!!errors.shipperName}
                        helperText={errors.shipperName?.message}
                        InputLabelProps={{ shrink: true }}
                        onChange={onChangeSetModified}
                      />
                    )}
                  />
                  <Stack direction="row" gap={1}>
                    <TextField
                      margin="dense"
                      type="text"
                      fullWidth
                      label="担当者"
                      variant="standard"
                      id="shipperContactPerson"
                      {...register('shipperContactPerson')}
                      error={!!errors.shipperContactPerson}
                      helperText={errors.shipperContactPerson?.message}
                      InputLabelProps={{ shrink: true }}
                      onChange={onChangeSetModified}
                    />
                    <TextField
                      margin="dense"
                      type="text"
                      fullWidth
                      label="電話番号"
                      variant="standard"
                      id="shipperPhoneNumber"
                      {...register('shipperPhoneNumber')}
                      error={'shipperPhoneNumber' in errors}
                      helperText={errors.shipperPhoneNumber?.message}
                      InputLabelProps={{ shrink: true }}
                      onChange={onChangeSetModified}
                    />
                    <TextField
                      margin="dense"
                      type="text"
                      fullWidth
                      label="メールアドレス"
                      variant="standard"
                      id="shipperEmailAddress"
                      {...register('shipperEmailAddress')}
                      error={'shipperEmailAddress' in errors}
                      helperText={errors.shipperEmailAddress?.message}
                      InputLabelProps={{ shrink: true }}
                      onChange={onChangeSetModified}
                    />
                  </Stack>
                </Stack>
              </Paper>

              <Paper>
                <Stack
                  pt={1}
                  pb={2}
                  px={2}
                  direction="row"
                  sx={{ alignContent: 'baseline' }}
                >
                  <Box sx={{ mr: 1 }}>頻度:</Box>
                  <RepeatSelect
                    handleSave={handleRepeatSave}
                    currentRule={recurrenceRule}
                    // date={minStartDate}
                  />
                </Stack>
              </Paper>

              <Paper>
                <Stack
                  pt={1}
                  pb={2}
                  px={2}
                >
                  <Autocomplete
                    freeSolo
                    options={filteredPickupPlaceData.map((d) => ({
                      ...d, label: d.name
                    }))}
                    onChange={loadingNameOnSelect}
                    multiple={false}
                    value={getValues('loadingName') ?? null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        margin="dense"
                        type="text"
                        fullWidth
                        variant="standard"
                        label="積地名"
                        id="loadingName"
                        {...register('loadingName')}
                        error={'loadingName' in errors}
                        helperText={errors.loadingName?.message}
                        InputLabelProps={{ shrink: true }}
                        onChange={onChangeSetModified}
                      />
                    )}
                  />
                  <TextField
                    margin="dense"
                    type="text"
                    fullWidth
                    variant="standard"
                    label="住所"
                    id="loadingAddress"
                    {...register('loadingAddress')}
                    error={'loadingAddress' in errors}
                    helperText={errors.loadingAddress?.message}
                    InputLabelProps={{ shrink: true }}
                    onChange={onChangeSetModified}
                  />
                  {
                    loadingFields.map((field, idx) => (
                      <Stack sx={{ mt: -2 }} key={['salesOrderLoadingSpecifiedDatetimesAttributes', 'Stack', idx].join('-')}>
                        <Stack direction="row" spacing={1} sx={{ position: 'relative', top: '20px', left: '90px', zIndex: 1000 }}>
                          {timeSettings.map((it) => (
                            <Button
                              key={`salesOrderLoadingSpecifiedDatetimesAttributes-button-${it.kind}`}
                              variant="contained"
                              sx={{ fontSize: '0.8em', p: 0, m: 0, }}
                              onClick={() => {
                                const startStr = getValues(`salesOrderLoadingSpecifiedDatetimesAttributes.${idx}.start_at`);
                                const startDt = new Date(startStr);
                                const startAt = it.startAt.split(':');
                                startDt.setHours(Number(startAt[0]));
                                startDt.setMinutes(Number(startAt[1]));
                                setValue(`salesOrderLoadingSpecifiedDatetimesAttributes.${idx}.start_at`, formatYMDHM(startDt));

                                const endStr = getValues(`salesOrderLoadingSpecifiedDatetimesAttributes.${idx}.end_at`);
                                const endDt = new Date(endStr);
                                const endAt = it.endAt.split(':');
                                endDt.setHours(Number(endAt[0]));
                                endDt.setMinutes(Number(endAt[1]));
                                setValue(`salesOrderLoadingSpecifiedDatetimesAttributes.${idx}.end_at`, formatYMDHM(endDt));
                              }}
                            >
                              {it.kind}
                            </Button>
                          ))}
                        </Stack>
                        <Stack
                          direction="row"
                          gap={1}
                          alignItems="baseline"
                          key={['loading_address', field.id, idx].join('-')}
                        >
                          <TextField
                            margin="dense"
                            type="datetime-local"
                            fullWidth
                            variant="standard"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            defaultValue={field.start_at}
                            label="受付開始時間"
                            id={`salesOrderLoadingSpecifiedDatetimesAttributes[${idx}].start_at`}
                            {...register(`salesOrderLoadingSpecifiedDatetimesAttributes.${idx}.start_at`)}
                            error={!!errors.salesOrderLoadingSpecifiedDatetimesAttributes?.[idx]?.start_at || !!loadingStartTimeError}
                            helperText={errors.salesOrderLoadingSpecifiedDatetimesAttributes?.[idx]?.start_at?.message || loadingStartTimeError}
                            onChange={onChangeSetModified}
                          />
                          <TextField
                            margin="dense"
                            type="datetime-local"
                            fullWidth
                            variant="standard"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            defaultValue={field.end_at}
                            label="受付終了時間"
                            id={`salesOrderLoadingSpecifiedDatetimesAttributes.${idx}.end_at`}
                            {...register(`salesOrderLoadingSpecifiedDatetimesAttributes.${idx}.end_at`)}
                            error={!!errors.salesOrderLoadingSpecifiedDatetimesAttributes?.[idx]?.end_at}
                            helperText={errors.salesOrderLoadingSpecifiedDatetimesAttributes?.[idx]?.end_at?.message}
                            onChange={onChangeSetModified}
                          />
                          <IconButton
                            disabled={loadingFields.length >= 3}
                            onClick={() => loadingAppend({
                              start_at: formatYMDHM(startOfDay(new Date())),
                              end_at: formatYMDHM(endOfDay(new Date()))
                            })}
                          >
                            <AddCircleOutlineOutlined />
                          </IconButton>
                          <IconButton
                            disabled={loadingFields.length < 2}
                            onClick={() => loadingRemove(idx)}
                          >
                            <RemoveCircleOutlineOutlined />
                          </IconButton>
                        </Stack>
                      </Stack>
                    ))
                  }
                  <Stack
                    direction="row"
                    gap={1}
                    alignItems="baseline"
                  >
                    <TextField
                      margin="dense"
                      type="number"
                      variant="standard"
                      id="loadingStayingMinutes"
                      label="滞在時間(分)"
                      {...register('loadingStayingMinutes')}
                      error={'loadingStayingMinutes' in errors}
                      helperText={errors.loadingStayingMinutes?.message}
                      InputLabelProps={{ shrink: true }}
                      onChange={onChangeSetModified}
                    />
                  </Stack>
                </Stack>
              </Paper>

              <Paper>
                <Stack
                  pt={1}
                  pb={2}
                  px={2}
                >
                  <Autocomplete
                    freeSolo
                    options={filteredDeliveryPlaceData.map((d) => ({
                      ...d, label: d.name
                    }))}
                    onChange={unloadingNameOnSelect}
                    multiple={false}
                    value={getValues('unloadingName') ?? null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        margin="dense"
                        type="text"
                        fullWidth
                        variant="standard"
                        label="降地名"
                        id="unloadingName"
                        {...register('unloadingName')}
                        error={'unloadingName' in errors}
                        helperText={errors.unloadingName?.message}
                        InputLabelProps={{ shrink: true }}
                        onChange={onChangeSetModified}
                      />
                    )}
                  />
                  <TextField
                    margin="dense"
                    type="text"
                    fullWidth
                    variant="standard"
                    label="住所"
                    id="unloadingAddress"
                    {...register('unloadingAddress')}
                    error={'unloadingAddress' in errors}
                    helperText={errors.unloadingAddress?.message}
                    InputLabelProps={{ shrink: true }}
                    onChange={onChangeSetModified}
                  />
                  {
                    unloadingFields.map((field, idx) => (
                      <Stack sx={{ mt: -2 }} key={['salesOrderUnloadingSpecifiedDatetimesAttributes-button-Stack', idx].join('-')}>
                        <Stack direction="row" spacing={1} sx={{ position: 'relative', top: '20px', left: '90px', zIndex: 1000 }}>
                          {timeSettings.map((it) => (
                            <Button
                              key={`salesOrderUnloadingSpecifiedDatetimesAttributes-button-${it.kind}`}
                              variant="contained"
                              sx={{ fontSize: '0.8em', p: 0, m: 0, }}
                              onClick={() => {
                                const startStr = getValues(`salesOrderUnloadingSpecifiedDatetimesAttributes.${idx}.start_at`);
                                const startDt = new Date(startStr);
                                const startAt = it.startAt.split(':');
                                startDt.setHours(Number(startAt[0]));
                                startDt.setMinutes(Number(startAt[1]));
                                setValue(`salesOrderUnloadingSpecifiedDatetimesAttributes.${idx}.start_at`, formatYMDHM(startDt));

                                const endStr = getValues(`salesOrderUnloadingSpecifiedDatetimesAttributes.${idx}.end_at`);
                                const endDt = new Date(endStr);
                                const endAt = it.endAt.split(':');
                                endDt.setHours(Number(endAt[0]));
                                endDt.setMinutes(Number(endAt[1]));
                                setValue(`salesOrderUnloadingSpecifiedDatetimesAttributes.${idx}.end_at`, formatYMDHM(endDt));
                              }}
                            >
                              {it.kind}
                            </Button>
                          ))}
                        </Stack>
                        <Stack
                          direction="row"
                          gap={1}
                          alignItems="baseline"
                          key={['unloadingAddress', field.id, idx].join('-')}
                        >
                          <TextField
                            margin="dense"
                            type="datetime-local"
                            fullWidth
                            variant="standard"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            defaultValue={field.start_at}
                            label="受付開始時間"
                            id={`salesOrderUnloadingSpecifiedDatetimesAttributes.${idx}.start_at`}
                            {...register(`salesOrderUnloadingSpecifiedDatetimesAttributes.${idx}.start_at`)}
                            error={!!errors.salesOrderUnloadingSpecifiedDatetimesAttributes?.[idx]?.start_at || !!unloadingStartTimeError}
                            helperText={errors.salesOrderUnloadingSpecifiedDatetimesAttributes?.[idx]?.start_at?.message || unloadingStartTimeError}
                            onChange={onChangeSetModified}
                          />
                          <TextField
                            margin="dense"
                            type="datetime-local"
                            fullWidth
                            variant="standard"
                            InputLabelProps={{
                              shrink: true,
                            }}
                            defaultValue={field.end_at}
                            label="受付終了時間"
                            id={`salesOrderUnloadingSpecifiedDatetimesAttributes.${idx}.end_at`}
                            {...register(`salesOrderUnloadingSpecifiedDatetimesAttributes.${idx}.end_at`)}
                            error={!!errors.salesOrderUnloadingSpecifiedDatetimesAttributes?.[idx]?.end_at || !!unloadingEndTimeError}
                            helperText={errors.salesOrderUnloadingSpecifiedDatetimesAttributes?.[idx]?.end_at?.message || unloadingEndTimeError}
                            onChange={onChangeSetModified}
                          />
                          <IconButton
                            disabled={unloadingFields.length >= 3}
                            onClick={() => unloadingAppend({
                              start_at: formatYMDHM(startOfDay(new Date())),
                              end_at: formatYMDHM(endOfDay(new Date()))
                            })}
                          >
                            <AddCircleOutlineOutlined />
                          </IconButton>
                          <IconButton
                            disabled={unloadingFields.length < 2}
                            onClick={() => unloadingRemove(idx)}
                          >
                            <RemoveCircleOutlineOutlined />
                          </IconButton>
                        </Stack>
                      </Stack>
                    ))
                  }
                  <Stack
                    direction="row"
                    gap={1}
                    alignItems="baseline"
                  >
                    <TextField
                      margin="dense"
                      type="number"
                      variant="standard"
                      id="unloadingStayingMinutes"
                      label="滞在時間(分)"
                      {...register('unloadingStayingMinutes')}
                      error={'unloadingStayingMinutes' in errors}
                      helperText={errors.unloadingStayingMinutes?.message}
                      InputLabelProps={{ shrink: true }}
                      onChange={onChangeSetModified}
                    />
                  </Stack>
                </Stack>
              </Paper>

              <Paper>
                <Stack
                  pt={1}
                  pb={2}
                  px={2}
                >
                  <Stack
                    direction="row"
                    gap={1}
                    alignItems="baseline"
                    justifyContent="space-between"
                  >
                    <Stack width="100%">
                      <FormControl
                        variant="standard"
                        fullWidth
                        size="small"
                      >
                        <InputLabel>荷姿</InputLabel>
                        <Controller
                          control={control}
                          name="itemPackingStyle"
                          render={({ field }) => (
                            <Select
                              id="itemPackingStyle"
                              margin="dense"
                              label="荷姿"
                              type="text"
                              fullWidth
                              variant="standard"
                              {...field}
                              value={itemPackingStyleValue ?? ''}
                              onChange={onChangeSetModified}
                            >
                              <MenuItem value="">&nbsp;</MenuItem>
                              <MenuItem value="ケース">ケース</MenuItem>
                              <MenuItem value="袋">袋</MenuItem>
                              <MenuItem value="はだか">はだか</MenuItem>
                              <MenuItem value="フレコンバック">フレコンバック</MenuItem>
                              <MenuItem value="ドラム類">ドラム類</MenuItem>
                              <MenuItem value="缶類">缶類</MenuItem>
                              <MenuItem value="パレット">パレット</MenuItem>
                              <MenuItem value="ラック">ラック</MenuItem>
                              <MenuItem value="ばら">ばら</MenuItem>
                            </Select>
                          )}
                        />
                      </FormControl>
                    </Stack>
                    <Stack width="100%">
                      <TextField
                        margin="dense"
                        type="number"
                        variant="standard"
                        fullWidth
                        id="itemCount"
                        label="数量"
                        {...register('itemCount')}
                        error={'itemCount' in errors}
                        helperText={errors.itemCount?.message}
                        InputLabelProps={{ shrink: true }}
                        onChange={onChangeSetModified}
                      />
                    </Stack>
                    <Stack width="100%">
                      <TextField
                        margin="dense"
                        type="number"
                        variant="standard"
                        fullWidth
                        id="itemTotalWeightKg"
                        label={`総重量(${licenseContext?.config?.unit})`}
                        {...register('itemTotalWeightKg')}
                        error={'itemTotalWeightKg' in errors}
                        helperText={errors.itemTotalWeightKg?.message}
                        InputLabelProps={{ shrink: true }}
                        onChange={onChangeSetModified}
                      />
                    </Stack>
                    {licenseContext?.config?.unit_used_for_calculation === '重量と体積' && (
                      <Stack width="100%">
                        <TextField
                          margin="dense"
                          type="number"
                          variant="standard"
                          fullWidth
                          label="総体積(m3)"
                          id="itemTotalVolumeM3"
                          {...register('itemTotalVolumeM3')}
                          error={'itemTotalVolumeM3' in errors}
                          helperText={errors.itemTotalVolumeM3?.message}
                          InputLabelProps={{ shrink: true }}
                          onChange={onChangeSetModified}
                        />
                      </Stack>
                    )}
                  </Stack>
                  <Stack direction="row" gap={1} alignItems="baseline">
                    <FormControl
                      variant="standard"
                      fullWidth
                    >
                      <InputLabel>輸送区分</InputLabel>
                      <Controller
                        control={control}
                        name="itemKlass"
                        render={({ field }) => (
                          <Select
                            id="itemKlass"
                            margin="dense"
                            label="輸送区分"
                            type="text"
                            fullWidth
                            variant="standard"
                            {...field}
                            value={itemKlass ?? ''}
                            onChange={onChangeSetModified}
                          >
                            <MenuItem value="">&nbsp;</MenuItem>
                            <MenuItem value="農産物">農産物</MenuItem>
                            <MenuItem value="畜産物">畜産物</MenuItem>
                            <MenuItem value="水産物">水産物</MenuItem>
                            <MenuItem value="食料品">食料品</MenuItem>
                            <MenuItem value="飲料品">飲料品</MenuItem>
                            <MenuItem value="木材">木材</MenuItem>
                            <MenuItem value="砂利・砂等">砂利・砂等</MenuItem>
                            <MenuItem value="金属製品">金属製品</MenuItem>
                            <MenuItem value="銅材">銅材</MenuItem>
                            <MenuItem value="建材">建材</MenuItem>
                            <MenuItem value="電気製品">電気製品</MenuItem>
                            <MenuItem value="機械・装置">機械・装置</MenuItem>
                            <MenuItem value="セメント">セメント</MenuItem>
                            <MenuItem value="セメント製品">セメント製品</MenuItem>
                            <MenuItem value="紙・パルプ製品">紙・パルプ製品</MenuItem>
                            <MenuItem value="石油製品">石油製品</MenuItem>
                            <MenuItem value="化学製品">化学製品</MenuItem>
                            <MenuItem value="その他危険物">その他危険物</MenuItem>
                            <MenuItem value="衣料・雑貨">衣料・雑貨</MenuItem>
                            <MenuItem value="引越貨物">引越貨物</MenuItem>
                          </Select>
                        )}
                      />
                    </FormControl>
                    <TextField
                      margin="dense"
                      type="text"
                      fullWidth
                      variant="standard"
                      label="品名"
                      id="itemName"
                      {...register('itemName')}
                      error={'itemName' in errors}
                      helperText={errors.itemName?.message}
                      InputLabelProps={{ shrink: true }}
                      onChange={onChangeSetModified}
                    />

                    <FormControl
                      variant="standard"
                      fullWidth
                    >
                      <InputLabel>荷扱い</InputLabel>
                      <Controller
                        control={control}
                        name="itemHandlingOfCargoStyle"
                        render={({ field }) => (
                          <Select
                            id="itemHandlingOfCargoStyle"
                            margin="dense"
                            label="荷扱い"
                            type="text"
                            fullWidth
                            variant="standard"
                            {...field}
                            value={itemHandlingOfCargoStyle || ''}
                            onChange={onChangeSetModified}
                          >
                            <MenuItem value="">&nbsp;</MenuItem>
                            <MenuItem value="手積">手積</MenuItem>
                            <MenuItem value="機械積">機械積</MenuItem>
                            <MenuItem value="機械積（フォーク）">機械積（フォーク）</MenuItem>
                            <MenuItem value="機械積（クレーン）">機械積（クレーン）</MenuItem>
                          </Select>
                        )}
                      />
                    </FormControl>

                    <FormControl
                      variant="standard"
                      fullWidth
                    >
                      <Controller
                        control={control}
                        name="itemCanBeMixed"
                        render={({ field }) => (
                          <FormControlLabel
                            {...field}
                            control={(
                              <Switch
                                checked={itemCanBeMixed}
                              />
                            )}
                            label="積み合わせ可"
                            labelPlacement="start"
                            onChange={onChangeSetModified}
                          />
                        )}
                      />
                    </FormControl>
                  </Stack>
                  <Stack direction="row" gap={1} alignItems="baseline">
                    <Stack>
                      <TextField
                        margin="dense"
                        type="number"
                        variant="standard"
                        fullWidth
                        id="truckCount"
                        label="必要台数"
                        {...register('truckCount')}
                        error={'truckCount' in errors}
                        helperText={errors.truckCount?.message}
                        InputLabelProps={{ shrink: true }}
                        onChange={onChangeSetModified}
                      />
                    </Stack>
                  </Stack>
                </Stack>
              </Paper>
              <Paper>
                <Stack
                  pt={1}
                  pb={2}
                  px={2}
                >
                  <FormControl component="fieldset">
                    <FormLabel component="legend">指定車両タイプ</FormLabel>
                    <FormGroup row>
                      <MultiCheckbox<SalesOrderResponseEntity, DesignatedTruckKlass>
                        form={form}
                        control={control}
                        name="designatedTruckKlasses"
                        options={[
                          '軽貨物',
                          '2t',
                          '4t',
                          '10t',
                          '20t',
                        ]}
                        defaultCheckedValues={designatedTruckKlasses}
                        onChange={onChangeSetModified}
                        valueSetter={setDesignatedTruckKlasses}
                      />
                    </FormGroup>
                  </FormControl>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">指定車種</FormLabel>
                    <FormGroup row>
                      <MultiCheckbox<SalesOrderResponseEntity, DesignatedTruckCarModel>
                        form={form}
                        control={control}
                        name="designatedTruckCarModels"
                        options={[
                          '平ボディ',
                          'バンボディ',
                          'ウィングボディ',
                          '保冷車',
                          '冷凍車',
                          '車載車',
                          '重機運搬車',
                          '危険物運搬車',
                          'ダンプ',
                          '幌',
                          'ユニック',
                          '海上コンテナー用'
                        ]}
                        defaultCheckedValues={designatedTruckCarModels}
                        onChange={onChangeSetModified}
                        valueSetter={setDesignatedTruckCarModels}
                      />
                    </FormGroup>
                  </FormControl>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">荷台高さ</FormLabel>
                    <FormGroup row>
                      <MultiCheckbox<SalesOrderResponseEntity, DesignatedTruckLoadingPlatformHeight>
                        form={form}
                        control={control}
                        name="designatedTruckLoadingPlatformHeights"
                        options={[
                          '標準',
                          '低床',
                          '中低床'
                        ]}
                        defaultCheckedValues={designatedTruckLoadingPlatformHeights}
                        onChange={onChangeSetModified}
                        valueSetter={setDesignatedTruckLoadingPlatformHeights}
                      />
                    </FormGroup>
                  </FormControl>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">荷台幅</FormLabel>
                    <FormGroup row>
                      <MultiCheckbox<SalesOrderResponseEntity, DesignatedTruckLoadingPlatformWidth>
                        form={form}
                        control={control}
                        name="designatedTruckLoadingPlatformWidths"
                        options={[
                          '標準',
                          'セミワイド',
                          'ワイド',
                        ]}
                        defaultCheckedValues={designatedTruckLoadingPlatformWidths}
                        onChange={onChangeSetModified}
                        valueSetter={setDesignatedTruckLoadingPlatformWidths}
                      />
                    </FormGroup>
                  </FormControl>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">荷台長さ</FormLabel>
                    <FormGroup row>
                      <MultiCheckbox<SalesOrderResponseEntity, DesignatedTruckLoadingPlatformLength>
                        form={form}
                        control={control}
                        name="designatedTruckLoadingPlatformLengths"
                        options={[
                          '大型',
                          '中型',
                          '小型',
                          'ショート',
                          'ロング'
                        ]}
                        defaultCheckedValues={designatedTruckLoadingPlatformLengths}
                        onChange={onChangeSetModified}
                        valueSetter={setDesignatedTruckLoadingPlatformLengths}
                      />
                    </FormGroup>
                  </FormControl>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">床仕様</FormLabel>
                    <FormGroup row>
                      <MultiCheckbox<SalesOrderResponseEntity, DesignatedTruckFloorSpecification>
                        form={form}
                        control={control}
                        name="designatedTruckFloorSpecifications"
                        options={[
                          '鉄板',
                          'ステンレス',
                          'ジョルダー',
                          '縞板'
                        ]}
                        defaultCheckedValues={designatedTruckFloorSpecifications}
                        onChange={onChangeSetModified}
                        valueSetter={setDesignatedTruckFloorSpecifications}
                      />
                    </FormGroup>
                  </FormControl>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">装置</FormLabel>
                    <FormGroup row>
                      <MultiCheckbox<SalesOrderResponseEntity, DesignatedTruckFeature>
                        form={form}
                        control={control}
                        name="designatedTruckFeatures"
                        options={[
                          'パワーゲート',
                          '空調',
                          'エアサス',
                          'スタンション'
                        ]}
                        defaultCheckedValues={designatedTruckFeatures}
                        onChange={onChangeSetModified}
                        valueSetter={setDesignatedTruckFeatures}
                      />
                    </FormGroup>
                  </FormControl>
                </Stack>
              </Paper>

              <Paper>
                <Stack
                  pt={1}
                  pb={2}
                  px={2}
                >
                  <Stack
                    direction="row"
                    gap={1}
                  >
                    <Typography variant="h6">
                      請求金額:
                    </Typography>
                    <Typography>
                      {`${totalBillingAmount}円`}
                    </Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    gap={1}
                  >
                    <TextField
                      margin="dense"
                      type="number"
                      variant="standard"
                      fullWidth
                      label="基本運賃"
                      id="salesOrderBillingAmountAttributes.basicFeeYen"
                      {...register('salesOrderBillingAmountAttributes.basicFeeYen')}
                      error={!!errors.salesOrderBillingAmountAttributes?.basicFeeYen}
                      helperText={errors.salesOrderBillingAmountAttributes?.basicFeeYen?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      margin="dense"
                      type="number"
                      variant="standard"
                      fullWidth
                      label="サーチャージ料"
                      id="salesOrderBillingAmountAttributes.surchargeFeeYen"
                      {...register('salesOrderBillingAmountAttributes.surchargeFeeYen')}
                      error={!!errors.salesOrderBillingAmountAttributes?.surchargeFeeYen}
                      helperText={errors.salesOrderBillingAmountAttributes?.surchargeFeeYen?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      margin="dense"
                      type="number"
                      variant="standard"
                      fullWidth
                      label="中継料"
                      id="salesOrderBillingAmountAttributes.relayFeeYen"
                      {...register('salesOrderBillingAmountAttributes.relayFeeYen')}
                      error={!!errors.salesOrderBillingAmountAttributes?.relayFeeYen}
                      helperText={errors.salesOrderBillingAmountAttributes?.relayFeeYen?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      margin="dense"
                      type="number"
                      variant="standard"
                      fullWidth
                      label="高速代"
                      id="salesOrderBillingAmountAttributes.highwayFeeYen"
                      {...register('salesOrderBillingAmountAttributes.highwayFeeYen')}
                      error={!!errors.salesOrderBillingAmountAttributes?.highwayFeeYen}
                      helperText={errors.salesOrderBillingAmountAttributes?.highwayFeeYen?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      margin="dense"
                      type="number"
                      variant="standard"
                      fullWidth
                      label="積込料"
                      id="salesOrderBillingAmountAttributes.loadingFeeYen"
                      {...register('salesOrderBillingAmountAttributes.loadingFeeYen')}
                      error={!!errors.salesOrderBillingAmountAttributes?.loadingFeeYen}
                      helperText={errors.salesOrderBillingAmountAttributes?.loadingFeeYen?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      margin="dense"
                      type="number"
                      variant="standard"
                      fullWidth
                      label="付帯業務料"
                      id="salesOrderBillingAmountAttributes.ancillaryFeeYen"
                      {...register('salesOrderBillingAmountAttributes.ancillaryFeeYen')}
                      error={!!errors.salesOrderBillingAmountAttributes?.ancillaryFeeYen}
                      helperText={errors.salesOrderBillingAmountAttributes?.ancillaryFeeYen?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      margin="dense"
                      type="number"
                      variant="standard"
                      fullWidth
                      label="待機時間料"
                      id="salesOrderBillingAmountAttributes.waitingTimeFeeYen"
                      {...register('salesOrderBillingAmountAttributes.waitingTimeFeeYen')}
                      error={!!errors.salesOrderBillingAmountAttributes?.waitingTimeFeeYen}
                      helperText={errors.salesOrderBillingAmountAttributes?.waitingTimeFeeYen?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      margin="dense"
                      type="number"
                      variant="standard"
                      fullWidth
                      label="取卸料"
                      id="salesOrderBillingAmountAttributes.unloadingFeeYen"
                      {...register('salesOrderBillingAmountAttributes.unloadingFeeYen')}
                      error={!!errors.salesOrderBillingAmountAttributes?.unloadingFeeYen}
                      helperText={errors.salesOrderBillingAmountAttributes?.unloadingFeeYen?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      margin="dense"
                      type="number"
                      variant="standard"
                      fullWidth
                      label="諸経費"
                      id="salesOrderBillingAmountAttributes.expensesFeeYen"
                      {...register('salesOrderBillingAmountAttributes.expensesFeeYen')}
                      error={!!errors.salesOrderBillingAmountAttributes?.expensesFeeYen}
                      helperText={errors.salesOrderBillingAmountAttributes?.expensesFeeYen?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Stack>
                  <TextField
                    {...register('salesOrderBillingAmountAttributes.ancillaryContent')}
                    error={!!errors.salesOrderBillingAmountAttributes?.ancillaryContent}
                    helperText={errors.salesOrderBillingAmountAttributes?.ancillaryContent?.message}
                    label="付帯業務内容"
                    size="small"
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                    onChange={onChangeSetModified}
                  />
                </Stack>
              </Paper>

              <Paper>
                <Stack
                  pt={1}
                  pb={2}
                  px={2}
                >
                  <Stack
                    direction="row"
                    gap={1}
                  >
                    <Typography variant="h6">
                      支払金額:
                    </Typography>
                    <Typography>
                      {`${totalPaymentAmount}円`}
                    </Typography>
                  </Stack>
                  <Stack
                    direction="row"
                    gap={1}
                  >
                    <TextField
                      margin="dense"
                      type="number"
                      variant="standard"
                      fullWidth
                      label="基本運賃"
                      id="salesOrderPaymentAmountAttributes.basicFeeYen"
                      {...register('salesOrderPaymentAmountAttributes.basicFeeYen')}
                      error={!!errors.salesOrderPaymentAmountAttributes?.basicFeeYen}
                      helperText={errors.salesOrderPaymentAmountAttributes?.basicFeeYen?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      margin="dense"
                      type="number"
                      variant="standard"
                      fullWidth
                      label="サーチャージ料"
                      id="salesOrderPaymentAmountAttributes.surchargeFeeYen"
                      {...register('salesOrderPaymentAmountAttributes.surchargeFeeYen')}
                      error={!!errors.salesOrderPaymentAmountAttributes?.surchargeFeeYen}
                      helperText={errors.salesOrderPaymentAmountAttributes?.surchargeFeeYen?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      margin="dense"
                      type="number"
                      variant="standard"
                      fullWidth
                      label="中継料"
                      id="salesOrderPaymentAmountAttributes.relayFeeYen"
                      {...register('salesOrderPaymentAmountAttributes.relayFeeYen')}
                      error={!!errors.salesOrderPaymentAmountAttributes?.relayFeeYen}
                      helperText={errors.salesOrderPaymentAmountAttributes?.relayFeeYen?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      margin="dense"
                      type="number"
                      variant="standard"
                      fullWidth
                      label="高速代"
                      id="salesOrderPaymentAmountAttributes.highwayFeeYen"
                      {...register('salesOrderPaymentAmountAttributes.highwayFeeYen')}
                      error={!!errors.salesOrderPaymentAmountAttributes?.highwayFeeYen}
                      helperText={errors.salesOrderPaymentAmountAttributes?.highwayFeeYen?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      margin="dense"
                      type="number"
                      variant="standard"
                      fullWidth
                      label="積込料"
                      id="salesOrderPaymentAmountAttributes.loadingFeeYen"
                      {...register('salesOrderPaymentAmountAttributes.loadingFeeYen')}
                      error={!!errors.salesOrderPaymentAmountAttributes?.loadingFeeYen}
                      helperText={errors.salesOrderPaymentAmountAttributes?.loadingFeeYen?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      margin="dense"
                      type="number"
                      variant="standard"
                      fullWidth
                      label="付帯業務料"
                      id="salesOrderPaymentAmountAttributes.ancillaryFeeYen"
                      {...register('salesOrderPaymentAmountAttributes.ancillaryFeeYen')}
                      error={!!errors.salesOrderPaymentAmountAttributes?.ancillaryFeeYen}
                      helperText={errors.salesOrderPaymentAmountAttributes?.ancillaryFeeYen?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      margin="dense"
                      type="number"
                      variant="standard"
                      fullWidth
                      label="待機時間料"
                      id="salesOrderPaymentAmountAttributes.waitingTimeFeeYen"
                      {...register('salesOrderPaymentAmountAttributes.waitingTimeFeeYen')}
                      error={!!errors.salesOrderPaymentAmountAttributes?.waitingTimeFeeYen}
                      helperText={errors.salesOrderPaymentAmountAttributes?.waitingTimeFeeYen?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      margin="dense"
                      type="number"
                      variant="standard"
                      fullWidth
                      label="取卸料"
                      id="salesOrderPaymentAmountAttributes.unloadingFeeYen"
                      {...register('salesOrderPaymentAmountAttributes.unloadingFeeYen')}
                      error={!!errors.salesOrderPaymentAmountAttributes?.unloadingFeeYen}
                      helperText={errors.salesOrderPaymentAmountAttributes?.unloadingFeeYen?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      margin="dense"
                      type="number"
                      variant="standard"
                      fullWidth
                      label="諸経費"
                      id="salesOrderPaymentAmountAttributes.expensesFeeYen"
                      {...register('salesOrderPaymentAmountAttributes.expensesFeeYen')}
                      error={!!errors.salesOrderPaymentAmountAttributes?.expensesFeeYen}
                      helperText={errors.salesOrderPaymentAmountAttributes?.expensesFeeYen?.message}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Stack>
                  <TextField
                    {...register('salesOrderPaymentAmountAttributes.ancillaryContent')}
                    error={!!errors.salesOrderPaymentAmountAttributes?.ancillaryContent}
                    helperText={errors.salesOrderPaymentAmountAttributes?.ancillaryContent?.message}
                    label="付帯業務内容"
                    size="small"
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                    onChange={onChangeSetModified}
                  />
                </Stack>
              </Paper>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'space-between', marginLeft: 1, marginRight: 3 }}>
            <Stack direction="row">
              {onClose && (
                <LoadingButton
                  loading={isLoading}
                  onClick={() => {
                    onClose();
                  }}
                >
                  キャンセル
                </LoadingButton>
              )}
              {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
              <LoadingButton startIcon={<SaveAsRoundedIcon />} onClick={form.handleSubmit(onSubmitSave)} variant="contained" loading={isLoading} loadingPosition="start">
                保存する
              </LoadingButton>
            </Stack>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default SalesOrderForm;
